import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import './ProfitSummary.css';
import PerformanceGauges from './PerformanceGauges';
import NoTargetsPlaceholder from './NoTargetsPlaceholder';
import { dayWindowUTC, weekWindowUTC, monthWindowUTC, rangeWindowUTC } from '../utils/dateWindow';

// Custom semi-circle gauge component for per-dealership dials
const SemiCircleGauge = ({ value, size = 140, strokeWidth = 12, color, children }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * Math.PI;
  const offset = circumference - (value / 100) * circumference;
  
  const colorMap = {
    orange: '#ff7a00',
    blue: '#00d4ff',
    emerald: '#10b981',
    coral: '#f97316'
  };
  
  // If color starts with #, use it directly; otherwise use colorMap
  const strokeColor = color && color.startsWith('#') ? color : (colorMap[color] || '#ff7a00');
  
  const ticks = [];
  let currentAngle = 0;
  let tickIndex = 0;
  
  while (currentAngle <= Math.PI) {
    const normalizedPosition = Math.abs(currentAngle - Math.PI / 2) / (Math.PI / 2);
    const minSpacing = 0.03;
    const maxSpacing = 0.15;
    const spacing = maxSpacing - (maxSpacing - minSpacing) * normalizedPosition;
    
    const minWidth = 0.8;
    const maxWidth = 2;
    const tickWidth = minWidth + (maxWidth - minWidth) * normalizedPosition;
    
    const tickLength = strokeWidth - 4;
    const outerRadius = radius + strokeWidth / 2 - 2;
    const innerRadius = outerRadius - tickLength;
    
    const x1 = size / 2 + outerRadius * Math.cos(Math.PI - currentAngle);
    const y1 = size / 2 - outerRadius * Math.sin(Math.PI - currentAngle);
    const x2 = size / 2 + innerRadius * Math.cos(Math.PI - currentAngle);
    const y2 = size / 2 - innerRadius * Math.sin(Math.PI - currentAngle);
    
    ticks.push(
      <line
        key={tickIndex}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="rgba(255, 255, 255, 0.2)"
        strokeWidth={tickWidth}
        strokeLinecap="round"
      />
    );
    
    currentAngle += spacing;
    tickIndex++;
  }
  
  return (
    <div style={{ position: 'relative', width: size, height: size / 2 + 40 }}>
      <svg width={size} height={size / 2 + 20} style={{ overflow: 'visible' }}>
        <path
          d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size / 2}`}
          fill="none"
          stroke="rgba(255, 255, 255, 0.1)"
          strokeWidth={strokeWidth}
          strokeLinecap="butt"
        />
        {ticks}
        <path
          d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size / 2}`}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="butt"
          style={{
            transition: 'stroke-dashoffset 1s ease',
            filter: `drop-shadow(0 0 4px ${strokeColor}40)`
          }}
        />
      </svg>
      <div style={{
        position: 'absolute',
        bottom: '0',
        left: 0,
        right: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {children}
      </div>
    </div>
  );
};

function Overview({ user, managedOwner, onNotificationsChange, showActivityAlerts }) {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState({
    serviceRevenue: 0,
    partsSold: 0,
    laborHours: 0,
    fiCloseRate: 0,
    totalDealerships: 0,
    totalROs: 0
  });
  const [fitSpecialistKpis, setFitSpecialistKpis] = useState({
    greets: { actual: 0, target: 0 },
    sitDowns: { actual: 0, target: 0 },
    testRides: { actual: 0, target: 0 },
    writeUps: { actual: 0, target: 0 },
    sales: { actual: 0, target: 0 }
  });
  const [serviceKpis, setServiceKpis] = useState({
    repairOrders: { actual: 0, target: 0, dealershipActual: 0, dealershipTarget: 0 },
    hours: { actual: 0, target: 0, dealershipActual: 0, dealershipTarget: 0 },
    netSales: { actual: 0, target: 0, dealershipActual: 0, dealershipTarget: 0 },
    grossProfit: { actual: 0, target: 0, dealershipActual: 0, dealershipTarget: 0 }
  });
  const [targets, setTargets] = useState(null);
  const [dealershipSettings, setDealershipSettings] = useState({
    performanceYearMode: 'CALENDAR',
    fiscalYearStartMonth: 1,
    fiscalYearStartDay: 1
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('monthly');
  // Month look-back: when timeRange === 'monthly', pick which month to view
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [displayYear, setDisplayYear] = useState(new Date().getFullYear());
  const monthPickerRef = useRef(null);
  const [dealerships, setDealerships] = useState([]); // from analytics (id,name)
  const [dealershipsMeta, setDealershipsMeta] = useState([]); // from user-management (id,name,status)
  const [dealershipMetrics, setDealershipMetrics] = useState([]); // per-dealership KPIs from analytics
  const [selectedDealerships, setSelectedDealerships] = useState([]);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [dealershipTargets, setDealershipTargets] = useState({}); // targets for each dealership { dealershipId: { frontEnd, fixedOps } }
  const [viewMode, setViewMode] = useState('personal'); // 'personal' or 'dealership'
  const [dealershipActuals, setDealershipActuals] = useState({
    greets: 0,
    sitDowns: 0,
    testRides: 0,
    writeUps: 0,
    sales: 0
  });
  const [selectedDealershipId, setSelectedDealershipId] = useState(null); // For fit specialists with multiple dealerships
  const [userDealerships, setUserDealerships] = useState([]); // List of dealerships for the user
  const [todaysDailyLog, setTodaysDailyLog] = useState(null); // Today's daily log for fit specialist
  const [todaysServiceEntry, setTodaysServiceEntry] = useState(null); // Today's service entry for service manager
  
  // Check if user is a Fit Specialist
  const userRoles = Array.isArray(user?.roles) ? user.roles : [];
  const isFitSpecialist = userRoles.includes('Fit Specialist') || userRoles.includes('FIT_SPECIALIST');
  const isServiceManager = userRoles.includes('Service Manager') || userRoles.includes('SERVICE_MANAGER');
  const isGm = userRoles.includes('General Manager') || userRoles.includes('GENERAL_MANAGER');
  // GMs get the same aggregate, owner-style Overview as owners / managed-owner views
  // (scoped to their dealership) instead of the single-dealership view. Other
  // non-owner roles keep their current view until role-specific dashboards land.
  const isOwnerView = user?.role === 'OWNER' || user?.role === 'owner' || !!managedOwner || isGm;
  
  // Get active dealership ID
  const activeDealershipId = selectedDealershipId || user?.dealershipId || (user?.dealerships && user.dealerships.length > 0 ? user.dealerships[0].id : null);

  // Month look-back helpers (used when timeRange === 'monthly')
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // True when the selected month is an earlier, already-completed month
  const isHistoricalMonth = () => {
    const now = new Date();
    return selectedMonth.getFullYear() < now.getFullYear() ||
      (selectedMonth.getFullYear() === now.getFullYear() && selectedMonth.getMonth() < now.getMonth());
  };

  const handleMonthSelect = (monthIndex) => {
    setSelectedMonth(new Date(displayYear, monthIndex, 1));
    setShowMonthPicker(false);
  };

  const handleCurrentMonth = () => {
    const now = new Date();
    setSelectedMonth(new Date(now.getFullYear(), now.getMonth(), 1));
    setDisplayYear(now.getFullYear());
    setShowMonthPicker(false);
  };

  const formatMonthDisplay = () => `${fullMonthNames[selectedMonth.getMonth()]} ${selectedMonth.getFullYear()}`;

  // Close the month picker when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (monthPickerRef.current && !monthPickerRef.current.contains(event.target)) {
        setShowMonthPicker(false);
      }
    };
    if (showMonthPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMonthPicker]);

  // Reusable month-picker control; only renders in monthly view
  const renderMonthPicker = () => {
    if (timeRange !== 'monthly') return null;
    return (
      <div className="date-picker-container overview-month-picker" ref={monthPickerRef}>
        <button
          className="date-picker-button"
          onClick={() => setShowMonthPicker(!showMonthPicker)}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <rect x="2" y="3" width="12" height="11" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M2 6h12M5 2v2M11 2v2" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
          {formatMonthDisplay()}
        </button>
        {showMonthPicker && (
          <div className="date-picker-dropdown month-picker-dropdown">
            <div className="calendar-header">
              <button onClick={() => setDisplayYear(displayYear - 1)}>‹</button>
              <span>{displayYear}</span>
              <button
                onClick={() => setDisplayYear(displayYear + 1)}
                disabled={displayYear >= new Date().getFullYear()}
              >›</button>
            </div>
            <div className="month-grid">
              {monthNames.map((month, index) => {
                const isSelected = index === selectedMonth.getMonth() && displayYear === selectedMonth.getFullYear();
                const isFuture = displayYear > new Date().getFullYear() ||
                  (displayYear === new Date().getFullYear() && index > new Date().getMonth());
                return (
                  <button
                    key={month}
                    className={`month-cell ${isSelected ? 'selected' : ''} ${isFuture ? 'disabled' : ''}`}
                    onClick={() => !isFuture && handleMonthSelect(index)}
                    disabled={isFuture}
                  >
                    {month}
                  </button>
                );
              })}
            </div>
            <button className="today-button" onClick={handleCurrentMonth}>
              Current Month
            </button>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    // Set up user dealerships for fit specialists and service managers
    const isScopedRole = (isFitSpecialist || isServiceManager);
    if (isScopedRole && user) {
      const dealershipsList = user.dealerships || (user.dealershipId ? [{ id: user.dealershipId, name: user.dealershipName }] : []);
      setUserDealerships(dealershipsList);
      if (!selectedDealershipId && dealershipsList.length > 0) {
        setSelectedDealershipId(dealershipsList[0].id);
      }
    }
  }, [user, isFitSpecialist, isServiceManager]);
  
  useEffect(() => {
    if ((isFitSpecialist || isServiceManager) && activeDealershipId) {
      setLoading(true);
      fetchDealershipSettings();
      fetchTargets();
    } else if (!isFitSpecialist && !isServiceManager) {
      fetchKPIs();
      fetchOwnerDealerships();
    }
  }, [activeDealershipId, isFitSpecialist, isServiceManager, managedOwner]);

  // Fetch KPIs when targets/settings are loaded or timeRange changes
  useEffect(() => {
    if (isFitSpecialist && targets) {
      fetchFitSpecialistKPIs();
    } else if (isServiceManager && targets) {
      fetchServiceManagerKPIs();
    } else if (!isFitSpecialist && !isServiceManager) {
      // Re-fetch KPIs when time range changes for owner/manager views
      fetchKPIs();
    }
  }, [targets, timeRange, selectedMonth, isFitSpecialist, isServiceManager, dealershipSettings]);

  // Helper to get the start of the current week (Monday)
  const getWeekStart = () => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // If Sunday, go back 6 days, otherwise go to Monday
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  };

  // Helper to get the end of the current week (Sunday)
  const getWeekEnd = () => {
    const monday = getWeekStart();
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return sunday;
  };

  // Helper to convert targets using actual fiscal year lengths
  const convertTarget = (targetValue, targetFrequency, displayFrequency) => {
    if (targetValue === undefined || targetValue === null) return 0;
    const fiscalMeta = getFiscalMeta();
    const fromFreq = (targetFrequency || 'yearly').toLowerCase();
    const toFreq = (displayFrequency || 'yearly').toLowerCase();

    let yearlyTotal;
    switch (fromFreq) {
      case 'daily':
        yearlyTotal = targetValue * fiscalMeta.daysInYear;
        break;
      case 'weekly':
        yearlyTotal = targetValue * fiscalMeta.weeksInYear;
        break;
      case 'monthly':
        yearlyTotal = targetValue * fiscalMeta.monthsInYear;
        break;
      case 'yearly':
      default:
        yearlyTotal = targetValue;
    }

    switch (toFreq) {
      case 'daily':
        return Math.round(yearlyTotal / fiscalMeta.daysInYear);
      case 'weekly':
        return Math.round(yearlyTotal / fiscalMeta.weeksInYear);
      case 'monthly':
        return Math.round(yearlyTotal / fiscalMeta.monthsInYear);
      case 'yearly':
      default:
        return Math.round(yearlyTotal);
    }
  };

  const fetchTargets = async () => {
    try {
      if (!activeDealershipId) {
        console.log('No dealership ID for user:', user);
        return;
      }
      
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/targets/${activeDealershipId}`;
      console.log('Fetching targets from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Targets response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Targets fetched successfully:', data);
        setTargets(data);
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Failed to fetch targets. Status:', response.status, 'Error:', errorData);
      }
    } catch (error) {
      console.error('Failed to fetch targets:', error);
    }
  };

  const fetchDealershipSettings = async () => {
    try {
      if (!activeDealershipId) {
        console.log('No dealership ID for user:', user);
        return;
      }

      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/dealerships/${activeDealershipId}`;
      console.log('Fetching dealership settings from:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const settings = data.dealership || data?.dealerships?.[0] || {};
        setDealershipSettings({
          performanceYearMode: settings.performanceYearMode || 'CALENDAR',
          fiscalYearStartMonth: settings.fiscalYearStartMonth || 1,
          fiscalYearStartDay: settings.fiscalYearStartDay || 1
        });
      } else {
        console.error('Failed to fetch dealership settings. Status:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch dealership settings:', error);
    }
  };

  const fetchFitSpecialistKPIs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!activeDealershipId) {
        setError('No dealership assigned to your account. Please contact your administrator.');
        setLoading(false);
        return;
      }

      // Get date range for actual data
      const { startDate, endDate } = getDateRange();
      const msPerDay = 1000 * 60 * 60 * 24;
      const startObj = new Date(startDate);
      const endObj = new Date(endDate);
      const daysElapsed = Math.max(1, Math.floor((endObj.getTime() - startObj.getTime()) / msPerDay) + 1);
      
      // Fetch actual performance data from daily logs
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/dealerships/${activeDealershipId}/daily-logs?userId=${user.id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const logs = data.entries || [];
        
        // Filter logs by date range - compare as YYYY-MM-DD strings to avoid timezone issues
        // Extract just the date portion from ISO strings
        const startDateStr = startDate.split('T')[0]; // YYYY-MM-DD
        const endDateStr = endDate.split('T')[0];     // YYYY-MM-DD
        
        const filteredLogs = logs.filter(log => {
          // log.date from database might be ISO string or Date object, get just the date part
          const logDateStr = typeof log.date === 'string' ? log.date.split('T')[0] : log.date.toISOString().split('T')[0];
          return logDateStr >= startDateStr && logDateStr <= endDateStr;
        });
        
        // Check if there's a log for today
        const todayStr = new Date().toISOString().split('T')[0];
        const todayLog = logs.find(log => {
          const logDateStr = typeof log.date === 'string' ? log.date.split('T')[0] : log.date.toISOString().split('T')[0];
          return logDateStr === todayStr;
        });
        setTodaysDailyLog(todayLog || null);
        
        // Aggregate the data for personal view
        const aggregated = {
          greets: 0,
          sitDowns: 0,
          testRides: 0,
          writeUps: 0,
          sales: 0
        };

        filteredLogs.forEach(log => {
          aggregated.greets += log.greets || 0;
          aggregated.sitDowns += log.sitDowns || 0;
          aggregated.testRides += log.testRides || 0;
          aggregated.writeUps += log.writeUps || 0;
          aggregated.sales += log.sales || 0;
        });
        
        // Fetch dealership-wide actuals (all logs for the dealership)
        const dealershipResponse = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/dealerships/${activeDealershipId}/daily-logs`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        // Aggregate dealership-wide data locally (don't rely on state)
        const dealershipAgg = {
          greets: 0,
          sitDowns: 0,
          testRides: 0,
          writeUps: 0,
          sales: 0
        };
        
        if (dealershipResponse.ok) {
          const dealershipData = await dealershipResponse.json();
          const allLogs = dealershipData.entries || [];
          
          // Filter by date range - compare as YYYY-MM-DD strings to avoid timezone issues
          const filteredAllLogs = allLogs.filter(log => {
            const logDateStr = typeof log.date === 'string' ? log.date.split('T')[0] : log.date.toISOString().split('T')[0];
            return logDateStr >= startDateStr && logDateStr <= endDateStr;
          });
          
          filteredAllLogs.forEach(log => {
            dealershipAgg.greets += log.greets || 0;
            dealershipAgg.sitDowns += log.sitDowns || 0;
            dealershipAgg.testRides += log.testRides || 0;
            dealershipAgg.writeUps += log.writeUps || 0;
            dealershipAgg.sales += log.sales || 0;
          });
          
          setDealershipActuals(dealershipAgg);
        }

        // Calculate targets based on frequency and employee allocation
        let calculatedTargets = {
          greets: 0,
          sitDowns: 0,
          testRides: 0,
          writeUps: 0,
          sales: 0
        };

        console.log('Targets data:', targets);
        console.log('User ID:', user.id);
        console.log('Time Range:', timeRange);

        if (targets && targets.frontEnd) {
          const frontEnd = targets.frontEnd;
          console.log('FrontEnd targets:', frontEnd);
          
          const metricsFreq = frontEnd.metricsFrequency || 'daily';
          const salesFreq = frontEnd.salesFrequency || frontEnd.frontEndFrequency || 'yearly';
          const allocation = frontEnd.employeeAllocations?.[user.id] || 0;
          const allocationPercent = allocation / 100;

          console.log('Metrics Frequency:', metricsFreq);
          console.log('Sales Frequency:', salesFreq);
          console.log('Allocation for user:', allocation);
          console.log('Raw target values:', {
            greets: frontEnd.greets,
            sitDowns: frontEnd.sitDowns,
            testRides: frontEnd.testRides,
            writeUps: frontEnd.writeUps,
            unitSales: frontEnd.unitSales
          });

          // Convert metrics to current time range and apply allocation percentage to ALL metrics
          calculatedTargets.greets = Math.round(convertTarget(frontEnd.greets || 0, metricsFreq, timeRange) * allocationPercent);
          calculatedTargets.sitDowns = Math.round(convertTarget(frontEnd.sitDowns || 0, metricsFreq, timeRange) * allocationPercent);
          calculatedTargets.testRides = Math.round(convertTarget(frontEnd.testRides || 0, metricsFreq, timeRange) * allocationPercent);
          calculatedTargets.writeUps = Math.round(convertTarget(frontEnd.writeUps || 0, metricsFreq, timeRange) * allocationPercent);
          
          // Sales target also uses allocation percentage
          const totalSalesTarget = convertTarget(frontEnd.unitSales || 0, salesFreq, timeRange);
          calculatedTargets.sales = Math.round(totalSalesTarget * allocationPercent);

          console.log('Calculated targets:', calculatedTargets);
          
          const dailyScale = timeRange === 'daily' ? Math.min(daysElapsed, 7) : 1;
          
          // Calculate dealership-wide targets (without allocation)
          const dealershipTargetsCalc = {
            greets: convertTarget(frontEnd.greets || 0, metricsFreq, timeRange) * dailyScale,
            sitDowns: convertTarget(frontEnd.sitDowns || 0, metricsFreq, timeRange) * dailyScale,
            testRides: convertTarget(frontEnd.testRides || 0, metricsFreq, timeRange) * dailyScale,
            writeUps: convertTarget(frontEnd.writeUps || 0, metricsFreq, timeRange) * dailyScale,
            sales: convertTarget(frontEnd.unitSales || 0, salesFreq, timeRange) * dailyScale
          };

          setFitSpecialistKpis({
            greets: { 
              actual: aggregated.greets, 
              target: calculatedTargets.greets * dailyScale,
              dealershipActual: dealershipAgg.greets,
              dealershipTarget: dealershipTargetsCalc.greets
            },
            sitDowns: { 
              actual: aggregated.sitDowns, 
              target: calculatedTargets.sitDowns * dailyScale,
              dealershipActual: dealershipAgg.sitDowns,
              dealershipTarget: dealershipTargetsCalc.sitDowns
            },
            testRides: { 
              actual: aggregated.testRides, 
              target: calculatedTargets.testRides * dailyScale,
              dealershipActual: dealershipAgg.testRides,
              dealershipTarget: dealershipTargetsCalc.testRides
            },
            writeUps: { 
              actual: aggregated.writeUps, 
              target: calculatedTargets.writeUps * dailyScale,
              dealershipActual: dealershipAgg.writeUps,
              dealershipTarget: dealershipTargetsCalc.writeUps
            },
            sales: { 
              actual: aggregated.sales, 
              target: calculatedTargets.sales * dailyScale,
              dealershipActual: dealershipAgg.sales,
              dealershipTarget: dealershipTargetsCalc.sales
            }
          });
        } else {
          console.log('No targets.frontEnd data available');
        }

        setError(null);
      } else {
        setError('Failed to load performance data');
      }
    } catch (error) {
      console.error('Failed to fetch Fit Specialist KPIs:', error);
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceManagerKPIs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (!activeDealershipId) {
        setError('No dealership assigned to your account. Please contact your administrator.');
        setLoading(false);
        return;
      }

      const { startDate, endDate } = getDateRange();
      const startDateStr = startDate.split('T')[0];
      const endDateStr = endDate.split('T')[0];

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/dealerships/${activeDealershipId}/service-jobs?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const entries = Array.isArray(data.entries) ? data.entries : [];

        // Filter by date range (server applies, keep client safety)
        const filtered = entries.filter((e) => {
          const d = typeof e.date === 'string' ? e.date.split('T')[0] : new Date(e.date).toISOString().split('T')[0];
          return d >= startDateStr && d <= endDateStr;
        });

        // Today's service entry for alert
        const todayStr = new Date().toISOString().split('T')[0];
        const todayEntry = entries.find((e) => {
          const d = typeof e.date === 'string' ? e.date.split('T')[0] : new Date(e.date).toISOString().split('T')[0];
          return d === todayStr;
        });
        setTodaysServiceEntry(todayEntry || null);

        // Aggregate actuals
        const sum = (arr, key) => arr.reduce((acc, x) => acc + (parseFloat(x[key]) || 0), 0);
        const roActual = sum(filtered, 'repairOrders');
        const hoursActual = sum(filtered, 'hours');
        const netSalesActual = sum(filtered, 'netSales');
        const grossProfitActual = sum(filtered, 'grossProfit');

        // Targets from fixed ops service - now using monthlyTargets
        const fiscalMeta = getFiscalMeta();
        const fixedOps = targets?.fixedOps || {};
        const monthlyTargets = fixedOps.monthlyTargets || [];

        // Calculate service targets based on time range
        const calculateServiceTargets = (monthlyTargets, timeRange, fiscalMeta) => {
          if (!monthlyTargets || monthlyTargets.length === 0) {
            return { roTarget: 0, hoursTarget: 0, netSalesTarget: 0, grossProfitTarget: 0 };
          }

          // Use the selected month for monthly view so looking back at a past
          // month shows that month's target; other views have no month picker
          // and always reflect the current month.
          const refDate = timeRange === 'monthly' ? selectedMonth : new Date();
          const currentMonth = refDate.getMonth(); // 0-based (0 = January)
          const currentYear = refDate.getFullYear();

          // Find selected month's data (monthlyTargets uses 1-based month numbering)
          const currentMonthData = monthlyTargets.find(m => m.month === currentMonth + 1);

          // For yearly: sum all months
          if (timeRange === 'yearly') {
            const totals = monthlyTargets.reduce((acc, month) => ({
              transactions: acc.transactions + (month.serviceTransactions || 0),
              hours: acc.hours + (month.serviceHours || 0),
              netSales: acc.netSales + (month.serviceNetSales || 0),
              grossProfit: acc.grossProfit + (month.serviceGrossProfit || 0)
            }), { transactions: 0, hours: 0, netSales: 0, grossProfit: 0 });

            return {
              roTarget: totals.transactions,
              hoursTarget: totals.hours,
              netSalesTarget: totals.netSales,
              grossProfitTarget: totals.grossProfit
            };
          }

          // For monthly: use specific month's target
          if (timeRange === 'monthly') {
            if (!currentMonthData) {
              return { roTarget: 0, hoursTarget: 0, netSalesTarget: 0, grossProfitTarget: 0 };
            }
            return {
              roTarget: currentMonthData.serviceTransactions || 0,
              hoursTarget: currentMonthData.serviceHours || 0,
              netSalesTarget: currentMonthData.serviceNetSales || 0,
              grossProfitTarget: currentMonthData.serviceGrossProfit || 0
            };
          }

          // For weekly: current month's target ÷ weeks in current month
          if (timeRange === 'weekly') {
            if (!currentMonthData) {
              return { roTarget: 0, hoursTarget: 0, netSalesTarget: 0, grossProfitTarget: 0 };
            }
            const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
            const weeksInMonth = daysInMonth / 7;

            return {
              roTarget: Math.round((currentMonthData.serviceTransactions || 0) / weeksInMonth),
              hoursTarget: Math.round((currentMonthData.serviceHours || 0) / weeksInMonth),
              netSalesTarget: Math.round((currentMonthData.serviceNetSales || 0) / weeksInMonth),
              grossProfitTarget: Math.round((currentMonthData.serviceGrossProfit || 0) / weeksInMonth)
            };
          }

          // For daily: current month's target ÷ days in current month
          if (timeRange === 'daily') {
            if (!currentMonthData) {
              return { roTarget: 0, hoursTarget: 0, netSalesTarget: 0, grossProfitTarget: 0 };
            }
            const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

            return {
              roTarget: Math.round((currentMonthData.serviceTransactions || 0) / daysInMonth),
              hoursTarget: Math.round((currentMonthData.serviceHours || 0) / daysInMonth),
              netSalesTarget: Math.round((currentMonthData.serviceNetSales || 0) / daysInMonth),
              grossProfitTarget: Math.round((currentMonthData.serviceGrossProfit || 0) / daysInMonth)
            };
          }

          return { roTarget: 0, hoursTarget: 0, netSalesTarget: 0, grossProfitTarget: 0 };
        };

        const serviceTargets = calculateServiceTargets(monthlyTargets, timeRange, fiscalMeta);
        const roTarget = serviceTargets.roTarget;
        const hoursTarget = serviceTargets.hoursTarget;
        const netSalesTarget = serviceTargets.netSalesTarget;
        const grossProfitTarget = serviceTargets.grossProfitTarget;

        setServiceKpis({
          repairOrders: {
            actual: roActual,
            target: roTarget,
            dealershipActual: roActual,
            dealershipTarget: roTarget
          },
          hours: {
            actual: hoursActual,
            target: hoursTarget,
            dealershipActual: hoursActual,
            dealershipTarget: hoursTarget
          },
          netSales: {
            actual: netSalesActual,
            target: netSalesTarget,
            dealershipActual: netSalesActual,
            dealershipTarget: netSalesTarget
          },
          grossProfit: {
            actual: grossProfitActual,
            target: grossProfitTarget,
            dealershipActual: grossProfitActual,
            dealershipTarget: grossProfitTarget
          }
        });

        setError(null);
      } else {
        setError('Failed to load service performance data');
      }
    } catch (error) {
      console.error('Failed to fetch Service Manager KPIs:', error);
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const getFiscalYearBounds = () => {
    const mode = dealershipSettings.performanceYearMode || 'CALENDAR';
    const startMonth = Math.min(Math.max(Number(dealershipSettings.fiscalYearStartMonth) || 1, 1), 12);
    const startDay = Math.min(Math.max(Number(dealershipSettings.fiscalYearStartDay) || 1, 1), 28);

    const today = new Date();
    let start = new Date(today.getFullYear(), startMonth - 1, startDay, 0, 0, 0, 0);

    if (mode === 'FISCAL' && today < start) {
      start.setFullYear(start.getFullYear() - 1);
    }

    const end = new Date(start);
    end.setFullYear(start.getFullYear() + 1);

    return { start, end };
  };

  const getFiscalMeta = () => {
    const { start, end } = getFiscalYearBounds();
    const msPerDay = 1000 * 60 * 60 * 24;
    const daysInYear = Math.round((end.getTime() - start.getTime()) / msPerDay);
    const weeksInYear = Math.ceil(daysInYear / 7);
    const monthsInYear = 12;
    const today = new Date();
    const daysElapsed = Math.max(0, Math.floor((today.getTime() - start.getTime()) / msPerDay));
    return { start, end, fiscalStart: start, daysInYear, weeksInYear, monthsInYear, daysElapsed };
  };

  // Boundaries are built in UTC from the local calendar dates the user picked,
  // because entry dates are stored as UTC calendar dates. Building them from
  // local midnight instead shifted the whole window east by the viewer's offset,
  // which dropped the 1st of the month and pulled in the next month's 1st.
  const getDateRange = () => {
    const now = new Date();
    const fiscalMeta = getFiscalMeta();
    let window;

    switch (timeRange) {
      case 'daily':
        window = dayWindowUTC(now);
        break;
      case 'weekly':
        // Weeks run Monday through Sunday on this view.
        window = weekWindowUTC(now, 1);
        break;
      case 'monthly':
        // Use the selected month (defaults to the current month) so users can look back
        window = monthWindowUTC(selectedMonth);
        break;
      case 'yearly':
        window = rangeWindowUTC(fiscalMeta.start, now);
        break;
      default: {
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        window = rangeWindowUTC(monthAgo, now);
      }
    }

    return { ...window, fiscalMeta };
  };

  const fetchKPIs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { startDate, endDate } = getDateRange();
      
      // Determine the correct endpoint based on user role (isOwnerView incl. GMs)
      let endpoint;

      if (isOwnerView) {
        // For owner role or when admin is managing an owner
        if (managedOwner?.id) {
          endpoint = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/analytics/owner/dashboard?startDate=${startDate}&endDate=${endDate}&ownerId=${managedOwner.id}`;
        } else {
          endpoint = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/analytics/owner/dashboard?startDate=${startDate}&endDate=${endDate}`;
        }
      } else {
        // For MANAGER and EMPLOYEE roles
        if (!user.dealershipId) {
          setError('No dealership assigned to your account. Please contact your administrator.');
          setLoading(false);
          return;
        }
        endpoint = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/analytics/dealerships/${user.dealershipId}/dashboard?startDate=${startDate}&endDate=${endDate}`;
      }
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        if (isOwnerView) {
          // Owner dashboard data structure
          // Calculate sales revenue from dealership performance data
          const salesRevenue = data.dealerships?.reduce((sum, d) => sum + (d.totalRevenue - d.serviceRevenue), 0) || 0;
          
          setKpis({
            salesRevenue: salesRevenue,
            serviceRevenue: data.totals?.totalServiceRevenue || 0,
            partsSold: data.totals?.totalUnits || 0,
            laborHours: data.totals?.totalLaborHours || 0,
            fiCloseRate: data.totals?.totalFIIncome || 0,
            totalDealerships: data.dealershipCount || 0,
            totalROs: 0 // Not available in owner totals
          });
          // Save dealerships list for filtering (normalize to id/name from owner dashboard shape)
          const dl = (data.dealerships || []).map(d => ({ id: d.dealershipId || d.id, name: d.dealershipName || d.name }));
          setDealerships(dl);
          setDealershipsMeta(prev => prev.length ? prev : dl); // fallback if meta not loaded
          
          // Save per-dealership metrics for column rendering (full dealership objects with sales/fixedOps)
          setDealershipMetrics(data.dealerships || []);
        } else {
          // Dealership dashboard data structure
          setKpis({
            salesRevenue: data.sales?.totalRevenue || 0,
            serviceRevenue: data.service?.totalRevenue || 0,
            partsSold: data.parts?.totalSales || 0,
            laborHours: data.service?.totalLaborHours || 0,
            fiCloseRate: data.fi?.penetrationRate || 0,
            totalROs: data.service?.totalJobs || 0
          });
        }
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Failed to fetch KPIs:', error);
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch owner's dealerships for filter panel
  const fetchOwnerDealerships = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/user-management/dealerships`;
      if (managedOwner?.id) {
        url += `?ownerId=${managedOwner.id}`;
      }
      const response = await fetch(url, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        // Include all fields including activity timestamps and displayOrder
        const list = (data.dealerships || []).map(d => ({
          id: d.id,
          name: d.name,
          status: d.status,
          displayOrder: d.displayOrder,
          lastDailyLog: d.lastDailyLog,
          lastServiceLog: d.lastServiceLog,
          lastPartsLog: d.lastPartsLog,
          lastApparelLog: d.lastApparelLog,
          lastApparelInventory: d.lastApparelInventory,
          lastPartsInventory: d.lastPartsInventory
        }));
        setDealershipsMeta(list);
        
        // Fetch targets for all dealerships
        await fetchAllDealershipTargets(list.map(d => d.id));
      }
    } catch (e) {
      console.warn('Failed to fetch owner dealerships for overview filter');
    }
  };

  // Fetch targets for all dealerships
  const fetchAllDealershipTargets = async (dealershipIds) => {
    try {
      const token = localStorage.getItem('token');
      const targetsMap = {};
      
      // Fetch targets for each dealership
      await Promise.all(dealershipIds.map(async (dealershipId) => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/targets/${dealershipId}`,
            {
              headers: { 'Authorization': `Bearer ${token}` }
            }
          );
          
          if (response.ok) {
            const data = await response.json();
            targetsMap[dealershipId] = {
              frontEnd: data.frontEnd || {},
              fixedOps: data.fixedOps || {}
            };
          }
        } catch (err) {
          console.warn(`Failed to fetch targets for dealership ${dealershipId}:`, err);
        }
      }));
      
      setDealershipTargets(targetsMap);
    } catch (error) {
      console.warn('Failed to fetch dealership targets:', error);
    }
  };

  // Push dealership activity status to the notification bell
  useEffect(() => {
    if (!onNotificationsChange) return;
    // Owners/managed-owner views always qualify; GMs qualify when Dashboard grants
    // it (Manage Users / Edit Dropdowns permission) via showActivityAlerts.
    if (!(user?.role === 'OWNER' || user?.role === 'owner') && !managedOwner && !showActivityAlerts) return;

    const metaList = dealershipsMeta.length ? dealershipsMeta : dealerships;
    if (!metaList.length) {
      onNotificationsChange([]);
      return;
    }

    const isToday = (date) => {
      if (!date) return false;
      const now = new Date();
      const dateOnly = date.split('T')[0];
      const [year, month, day] = dateOnly.split('-').map(Number);
      return now.getFullYear() === year && now.getMonth() === month - 1 && now.getDate() === day;
    };

    const isThisWeek = (date) => {
      if (!date) return false;
      const now = new Date();
      const dateObj = new Date(date);
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay());
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);
      return dateObj >= weekStart && dateObj <= weekEnd;
    };

    const statusByDealership = metaList.map(d => ({
      name: d.name,
      salesLogged: isToday(d.lastDailyLog),
      fixedOpsLogged: isToday(d.lastServiceLog) || isToday(d.lastPartsLog) || isToday(d.lastApparelLog),
      inventoryLogged: isThisWeek(d.lastApparelInventory) || isThisWeek(d.lastPartsInventory)
    }));

    const allGood = statusByDealership.every(d => d.salesLogged && d.fixedOpsLogged && d.inventoryLogged);

    onNotificationsChange([{
      id: 'dealership-activity',
      type: allGood ? 'success' : 'warning',
      title: allGood ? 'All dealerships on track' : 'Dealership activity incomplete',
      message: allGood
        ? 'All dealerships have completed Sales and Fixed Operations logs for today, and Inventory tracking for this week.'
        : null,
      dealershipStatus: allGood ? null : statusByDealership
    }]);
  }, [dealershipsMeta, dealerships, managedOwner, user?.role, onNotificationsChange]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatPercent = (value) => {
    return `${value.toFixed(1)}%`;
  };

  const abbreviateNumber = (value) => {
    if (value === 0) return '0';
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';

    if (absValue >= 1000000000) {
      return `${sign}${(absValue / 1000000000).toFixed(1)}B`;
    } else if (absValue >= 1000000) {
      return `${sign}${(absValue / 1000000).toFixed(1)}M`;
    } else if (absValue >= 1000) {
      return `${sign}${(absValue / 1000).toFixed(0)}k`;
    }
    return `${sign}${absValue.toFixed(0)}`;
  };

  // Calculate fiscal year metadata with days/weeks/months remaining
  const calculateFiscalMeta = (dealershipId) => {
    // Get dealership settings - prioritize dealershipData which has actual fiscal settings
    const dealershipData = dealerships.find(d => d.id === dealershipId);
    const metricsData = dealershipMetrics.find(dm => dm.dealershipId === dealershipId);
    
    // Use dealership-specific settings from dealershipData first, then metricsData, then defaults
    const mode = dealershipData?.performanceYearMode || metricsData?.performanceYearMode || 'CALENDAR';
    const startMonth = Math.min(Math.max(Number(dealershipData?.fiscalYearStartMonth || metricsData?.fiscalYearStartMonth || 1), 1), 12);
    const startDay = Math.min(Math.max(Number(dealershipData?.fiscalYearStartDay || metricsData?.fiscalYearStartDay || 1), 1), 28);

    const today = new Date();
    let fiscalStart = new Date(today.getFullYear(), startMonth - 1, startDay, 0, 0, 0, 0);

    if (mode === 'FISCAL' && today < fiscalStart) {
      fiscalStart.setFullYear(fiscalStart.getFullYear() - 1);
    }

    const fiscalEnd = new Date(fiscalStart);
    fiscalEnd.setFullYear(fiscalStart.getFullYear() + 1);

    const msPerDay = 1000 * 60 * 60 * 24;
    const daysInYear = Math.round((fiscalEnd.getTime() - fiscalStart.getTime()) / msPerDay);
    const daysElapsed = Math.max(0, Math.floor((today.getTime() - fiscalStart.getTime()) / msPerDay));
    const daysRemaining = Math.max(0, daysInYear - daysElapsed);
    const weeksInYear = Math.ceil(daysInYear / 7);
    const weeksRemaining = Math.max(0, Math.ceil(daysRemaining / 7));
    const monthsInYear = 12;

    return {
      fiscalStart,
      fiscalEnd,
      daysInYear,
      daysElapsed,
      daysRemaining,
      weeksInYear,
      weeksRemaining,
      monthsInYear
    };
  };

  // Calculate if on track based on elapsed time vs progress
  const calculateTrendIndicator = (actual, target, passedFiscalMeta) => {
    if (!target || target === 0) return { status: 'neutral', color: 'rgba(255,255,255,0.5)' };
    
    const fiscalMeta = passedFiscalMeta || getFiscalMeta();
    if (!fiscalMeta || !fiscalMeta.fiscalStart) {
      return { status: 'neutral', color: 'rgba(255,255,255,0.5)' };
    }
    
    const today = new Date();
    
    let expectedProgressPercent = 0;
    let actualProgressPercent = (actual / target) * 100;
    
    switch (timeRange) {
      case 'daily':
        // For daily, we're at 100% of the day already
        expectedProgressPercent = 100;
        break;
      case 'weekly':
        // Calculate day of week (0 = Sun, 1 = Mon)
        {
          const dayOfWeek = today.getDay();
          const daysIntoWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          expectedProgressPercent = ((daysIntoWeek + 1) / 7) * 100;
        }
        break;
      case 'monthly':
        // For a completed past month the full month has elapsed (100% expected);
        // for the current month, pace against days elapsed.
        if (isHistoricalMonth()) {
          expectedProgressPercent = 100;
        } else {
          const daysIntoMonth = today.getDate();
          const daysInCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
          expectedProgressPercent = (daysIntoMonth / daysInCurrentMonth) * 100;
        }
        break;
      case 'yearly':
        // For yearly, align first fiscal month pacing with monthly expectation
        {
          const fiscalStartMonth = fiscalMeta.fiscalStart.getMonth();
          const fiscalStartYear = fiscalMeta.fiscalStart.getFullYear();
          const isFirstFiscalMonth = today.getFullYear() === fiscalStartYear && today.getMonth() === fiscalStartMonth;
          if (isFirstFiscalMonth) {
            const daysIntoMonth = today.getDate();
            const daysInCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
            // Scale monthly pacing to yearly target proportion (month slice of the year)
            expectedProgressPercent = ((daysIntoMonth / daysInCurrentMonth) / fiscalMeta.monthsInYear) * 100;
          } else {
            expectedProgressPercent = (fiscalMeta.daysElapsed / fiscalMeta.daysInYear) * 100;
          }
        }
        break;
      default:
        expectedProgressPercent = 100;
    }
    
    // Compare actual progress to expected progress
    const performanceRatio = actualProgressPercent / expectedProgressPercent;
    
    if (performanceRatio >= 0.9) {
      return { status: 'onTrack', color: '#10b981' }; // Green - on track or ahead
    } else if (performanceRatio >= 0.7) {
      return { status: 'caution', color: '#f59e0b' }; // Orange - slight behind
    } else {
      return { status: 'behind', color: '#ef4444' }; // Red - significantly behind
    }
  };

  // Get trend indicator SVG (up triangle for on track, down for behind)
  const TrendIndicator = ({ actual, target, fiscalMeta }) => {
    const trend = calculateTrendIndicator(actual, target, fiscalMeta);
    const isOnTrack = trend.status === 'onTrack';
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <svg width="16" height="16" viewBox="0 0 16 16" style={{ marginBottom: isOnTrack ? '2px' : '0px' }}>
          {isOnTrack ? (
            // Up triangle
            <polygon points="8,2 14,12 2,12" fill={trend.color} />
          ) : (
            // Down triangle
            <polygon points="2,4 14,4 8,14" fill={trend.color} />
          )}
        </svg>
        <span style={{ fontSize: '12px', color: trend.color, fontWeight: '600' }}>
          {trend.status === 'onTrack' ? 'On Track' : trend.status === 'caution' ? 'Caution' : 'Behind'}
        </span>
      </div>
    );
  };

  // Convert target value from stored frequency to display frequency
  const convertTargetValue = (value, storedFrequency, displayFrequency, fiscalMeta) => {
    if (!value || value === 0) return 0;
    
    const fromFreq = (storedFrequency || 'yearly').toLowerCase();
    const toFreq = (displayFrequency || 'yearly').toLowerCase();
    
    // Convert to yearly first
    let yearlyTotal;
    switch (fromFreq) {
      case 'daily':
        yearlyTotal = value * fiscalMeta.daysInYear;
        break;
      case 'weekly':
        yearlyTotal = value * fiscalMeta.weeksInYear;
        break;
      case 'monthly':
        yearlyTotal = value * fiscalMeta.monthsInYear;
        break;
      case 'yearly':
      default:
        yearlyTotal = value;
    }
    
    // Guard against rounding down to 0 when a positive yearly target exists (especially in daily view)
    if (yearlyTotal <= 0) return 0;

    const safeRound = (divisor) => Math.max(1, Math.round(yearlyTotal / divisor));
    
    // Convert from yearly to display frequency
    // Note: Display frequency should match the time period shown (not remaining time)
    switch (toFreq) {
      case 'daily':
        // Daily target = yearly / total days in year (what you need per day on average)
        return safeRound(fiscalMeta.daysInYear);
      case 'weekly':
        // Weekly target = yearly / weeks in year
        return safeRound(fiscalMeta.weeksInYear);
      case 'monthly':
        // Monthly target = yearly / 12
        return safeRound(fiscalMeta.monthsInYear);
      case 'yearly':
      default:
        // For yearly, we show year-to-date progress vs full year target
        return Math.round(yearlyTotal);
    }
  };

  if (loading) {
    return (
      <div className="view-header">
        <h1 className="view-title">Overview</h1>
        <p className="view-subtitle">Loading dashboard data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="view-header">
          <h1 className="view-title">Overview</h1>
          <p className="view-subtitle">Unable to load dashboard data</p>
        </div>
        <div className="content-card">
          <p style={{ color: '#ef4444' }}>{error}</p>
          <button 
            onClick={fetchKPIs} 
            className="submit-btn" 
            style={{ marginTop: '16px', width: 'auto' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if ((isFitSpecialist || isServiceManager) && targets) {
    const frontEndConfigured = Array.isArray(targets?.frontEnd?.monthlyTargets) && targets.frontEnd.monthlyTargets.length > 0;
    const fixedOpsConfigured = Array.isArray(targets?.fixedOps?.monthlyTargets) && targets.fixedOps.monthlyTargets.length > 0;
    if ((isFitSpecialist && !frontEndConfigured) || (isServiceManager && !fixedOpsConfigured)) {
      return (
        <div>
          <div className="view-header">
            <h1 className="view-title">Overview</h1>
          </div>
          <NoTargetsPlaceholder />
        </div>
      );
    }
  }

  // Render Service Manager overview
  if (isServiceManager) {
    const calculateProgress = (actual, target) => {
      if (!target || target === 0) return 0;
      return Math.min(Math.round((actual / target) * 100), 100);
    };

    const getProgressColor = (progress) => {
      if (progress >= 90) return '#10b981';
      if (progress >= 70) return '#f59e0b';
      return '#ef4444';
    };

    return (
      <div>
        <div className="view-header">
          <h1 className="view-title">Overview</h1>
          <p className="view-subtitle">Service department performance</p>
        </div>

        {userDealerships.length > 1 && (
          <div style={{ 
            margin: '0 0 16px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <label style={{ 
              fontSize: '14px',
              fontWeight: '500',
              color: 'rgba(255,255,255,0.8)',
              minWidth: 'fit-content'
            }}>
              Viewing:
            </label>
            <select 
              value={selectedDealershipId || ''}
              onChange={(e) => setSelectedDealershipId(e.target.value)}
              style={{
                padding: '10px 16px',
                paddingRight: '40px',
                borderRadius: '6px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.08)',
                color: 'white',
                fontSize: '15px',
                fontWeight: '500',
                cursor: 'pointer',
                outline: 'none',
                transition: 'all 0.2s ease',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='rgba(255,255,255,0.6)' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                minWidth: '250px'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
            >
              {userDealerships.map(d => (
                <option key={d.id} value={d.id} style={{ background: '#1a1a1a', padding: '8px' }}>{d.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="content-card">
          <h2 className="card-title">Alerts</h2>
          {todaysServiceEntry ? (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              padding: '12px',
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(76, 175, 80, 0.3)'
            }}>
              <div style={{ fontSize: '20px', color: '#4caf50' }}>✓</div>
              <div>
                <div style={{ color: '#4caf50', fontWeight: '600', marginBottom: '4px' }}>Service Entry Complete</div>
                <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>
                  {formatNumber(todaysServiceEntry.repairOrders || 0)} ROs • {formatNumber(todaysServiceEntry.hours || 0)} hrs • {formatCurrency(todaysServiceEntry.netSales || 0)} net • {formatCurrency(todaysServiceEntry.grossProfit || 0)} GP
                </div>
              </div>
            </div>
          ) : (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              gap: '12px',
              padding: '12px',
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 152, 0, 0.3)'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#ff9800', fontWeight: '600', marginBottom: '4px' }}>⚠ No Service Entry Today</div>
                <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>Log today’s service performance to track progress</div>
              </div>
              <button
                onClick={() => navigate(`/service-input?new=true${selectedDealershipId ? `&dealershipId=${selectedDealershipId}` : ''}`)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ff9800',
                  color: '#000',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#ffb74d'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#ff9800'}
              >
                Log Service
              </button>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap', padding: '0 16px', marginBottom: '24px' }}>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>Time Period</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div className="time-range-selector">
                <button
                  className={`time-range-btn ${timeRange === 'daily' ? 'active' : ''}`}
                  onClick={() => setTimeRange('daily')}
                >
                  Daily
                </button>
                <button
                  className={`time-range-btn ${timeRange === 'weekly' ? 'active' : ''}`}
                  onClick={() => setTimeRange('weekly')}
                >
                  Weekly
                </button>
                <button
                  className={`time-range-btn ${timeRange === 'monthly' ? 'active' : ''}`}
                  onClick={() => setTimeRange('monthly')}
                >
                  Monthly
                </button>
                <button
                  className={`time-range-btn ${timeRange === 'yearly' ? 'active' : ''}`}
                  onClick={() => setTimeRange('yearly')}
                >
                  Yearly
                </button>
              </div>
              {renderMonthPicker()}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>View Mode</div>
            <div className="time-range-selector">
              <button 
                className={`time-range-btn ${viewMode === 'personal' ? 'active' : ''}`}
                onClick={() => setViewMode('personal')}
              >
                My Targets
              </button>
              <button 
                className={`time-range-btn ${viewMode === 'dealership' ? 'active' : ''}`}
                onClick={() => setViewMode('dealership')}
              >
                Dealership Targets
              </button>
            </div>
          </div>
        </div>

        <div className="kpi-grid">
          {/* Repair Orders */}
          <div className="kpi-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div className="kpi-label">Repair Orders</div>
              <TrendIndicator 
                actual={viewMode === 'personal' ? serviceKpis.repairOrders.actual : serviceKpis.repairOrders.dealershipActual}
                target={viewMode === 'personal' ? serviceKpis.repairOrders.target : serviceKpis.repairOrders.dealershipTarget}
                fiscalMeta={getFiscalMeta()}
              />
            </div>
            <div className="kpi-value">{formatNumber(viewMode === 'personal' ? serviceKpis.repairOrders.actual : serviceKpis.repairOrders.dealershipActual)}</div>
            <div style={{ marginTop: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
              Target: {formatNumber(viewMode === 'personal' ? serviceKpis.repairOrders.target : serviceKpis.repairOrders.dealershipTarget)}
            </div>
            <div style={{ marginTop: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
              <div style={{ 
                width: `${calculateProgress(
                  viewMode === 'personal' ? serviceKpis.repairOrders.actual : serviceKpis.repairOrders.dealershipActual, 
                  viewMode === 'personal' ? serviceKpis.repairOrders.target : serviceKpis.repairOrders.dealershipTarget
                )}%`, 
                height: '100%', 
                background: getProgressColor(calculateProgress(
                  viewMode === 'personal' ? serviceKpis.repairOrders.actual : serviceKpis.repairOrders.dealershipActual, 
                  viewMode === 'personal' ? serviceKpis.repairOrders.target : serviceKpis.repairOrders.dealershipTarget
                )),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ marginTop: '4px', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
              {calculateProgress(
                viewMode === 'personal' ? serviceKpis.repairOrders.actual : serviceKpis.repairOrders.dealershipActual, 
                viewMode === 'personal' ? serviceKpis.repairOrders.target : serviceKpis.repairOrders.dealershipTarget
              )}% of target
            </div>
          </div>

          {/* Hours */}
          <div className="kpi-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div className="kpi-label">Hours</div>
              <TrendIndicator 
                actual={viewMode === 'personal' ? serviceKpis.hours.actual : serviceKpis.hours.dealershipActual}
                target={viewMode === 'personal' ? serviceKpis.hours.target : serviceKpis.hours.dealershipTarget}
                fiscalMeta={getFiscalMeta()}
              />
            </div>
            <div className="kpi-value">{formatNumber(viewMode === 'personal' ? serviceKpis.hours.actual : serviceKpis.hours.dealershipActual)}</div>
            <div style={{ marginTop: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
              Target: {formatNumber(viewMode === 'personal' ? serviceKpis.hours.target : serviceKpis.hours.dealershipTarget)}
            </div>
            <div style={{ marginTop: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
              <div style={{ 
                width: `${calculateProgress(
                  viewMode === 'personal' ? serviceKpis.hours.actual : serviceKpis.hours.dealershipActual, 
                  viewMode === 'personal' ? serviceKpis.hours.target : serviceKpis.hours.dealershipTarget
                )}%`, 
                height: '100%', 
                background: getProgressColor(calculateProgress(
                  viewMode === 'personal' ? serviceKpis.hours.actual : serviceKpis.hours.dealershipActual, 
                  viewMode === 'personal' ? serviceKpis.hours.target : serviceKpis.hours.dealershipTarget
                )),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ marginTop: '4px', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
              {calculateProgress(
                viewMode === 'personal' ? serviceKpis.hours.actual : serviceKpis.hours.dealershipActual, 
                viewMode === 'personal' ? serviceKpis.hours.target : serviceKpis.hours.dealershipTarget
              )}% of target
            </div>
          </div>

          {/* Net Sales */}
          <div className="kpi-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div className="kpi-label">Net Sales</div>
              <TrendIndicator 
                actual={viewMode === 'personal' ? serviceKpis.netSales.actual : serviceKpis.netSales.dealershipActual}
                target={viewMode === 'personal' ? serviceKpis.netSales.target : serviceKpis.netSales.dealershipTarget}
                fiscalMeta={getFiscalMeta()}
              />
            </div>
            <div className="kpi-value">{formatCurrency(viewMode === 'personal' ? serviceKpis.netSales.actual : serviceKpis.netSales.dealershipActual)}</div>
            <div style={{ marginTop: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
              Target: {formatCurrency(viewMode === 'personal' ? serviceKpis.netSales.target : serviceKpis.netSales.dealershipTarget)}
            </div>
            <div style={{ marginTop: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
              <div style={{ 
                width: `${calculateProgress(
                  viewMode === 'personal' ? serviceKpis.netSales.actual : serviceKpis.netSales.dealershipActual, 
                  viewMode === 'personal' ? serviceKpis.netSales.target : serviceKpis.netSales.dealershipTarget
                )}%`, 
                height: '100%', 
                background: getProgressColor(calculateProgress(
                  viewMode === 'personal' ? serviceKpis.netSales.actual : serviceKpis.netSales.dealershipActual, 
                  viewMode === 'personal' ? serviceKpis.netSales.target : serviceKpis.netSales.dealershipTarget
                )),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ marginTop: '4px', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
              {calculateProgress(
                viewMode === 'personal' ? serviceKpis.netSales.actual : serviceKpis.netSales.dealershipActual, 
                viewMode === 'personal' ? serviceKpis.netSales.target : serviceKpis.netSales.dealershipTarget
              )}% of target
            </div>
          </div>

          {/* Gross Profit */}
          <div className="kpi-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div className="kpi-label">Gross Profit</div>
              <TrendIndicator 
                actual={viewMode === 'personal' ? serviceKpis.grossProfit.actual : serviceKpis.grossProfit.dealershipActual}
                target={viewMode === 'personal' ? serviceKpis.grossProfit.target : serviceKpis.grossProfit.dealershipTarget}
                fiscalMeta={getFiscalMeta()}
              />
            </div>
            <div className="kpi-value">{formatCurrency(viewMode === 'personal' ? serviceKpis.grossProfit.actual : serviceKpis.grossProfit.dealershipActual)}</div>
            <div style={{ marginTop: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
              Target: {formatCurrency(viewMode === 'personal' ? serviceKpis.grossProfit.target : serviceKpis.grossProfit.dealershipTarget)}
            </div>
            <div style={{ marginTop: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
              <div style={{ 
                width: `${calculateProgress(
                  viewMode === 'personal' ? serviceKpis.grossProfit.actual : serviceKpis.grossProfit.dealershipActual, 
                  viewMode === 'personal' ? serviceKpis.grossProfit.target : serviceKpis.grossProfit.dealershipTarget
                )}%`, 
                height: '100%', 
                background: getProgressColor(calculateProgress(
                  viewMode === 'personal' ? serviceKpis.grossProfit.actual : serviceKpis.grossProfit.dealershipActual, 
                  viewMode === 'personal' ? serviceKpis.grossProfit.target : serviceKpis.grossProfit.dealershipTarget
                )),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ marginTop: '4px', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
              {calculateProgress(
                viewMode === 'personal' ? serviceKpis.grossProfit.actual : serviceKpis.grossProfit.dealershipActual, 
                viewMode === 'personal' ? serviceKpis.grossProfit.target : serviceKpis.grossProfit.dealershipTarget
              )}% of target
            </div>
          </div>
        </div>

        <div className="content-card">
          <h2 className="card-title">Performance Summary</h2>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: '8px 0 0 0', fontSize: '14px' }}>
            Review daily service activity and targets to stay on pace.
          </p>
        </div>
      </div>
    );
  }

  // Render Fit Specialist specific overview
  if (isFitSpecialist) {
    const calculateProgress = (actual, target) => {
      if (!target || target === 0) return 0;
      return Math.min(Math.round((actual / target) * 100), 100);
    };

    const getProgressColor = (progress) => {
      if (progress >= 90) return '#10b981'; // green
      if (progress >= 70) return '#f59e0b'; // orange
      return '#ef4444'; // red
    };

    return (
      <div>
        <div className="view-header">
          <h1 className="view-title">Overview</h1>
          <p className="view-subtitle">{viewMode === 'personal' ? 'Your performance metrics and targets' : 'Dealership performance metrics and targets'}</p>
          
          {userDealerships.length > 1 && (
            <div style={{ 
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <label style={{ 
                fontSize: '14px',
                fontWeight: '500',
                color: 'rgba(255,255,255,0.8)',
                minWidth: 'fit-content'
              }}>
                Viewing:
              </label>
              <select 
                value={selectedDealershipId || ''}
                onChange={(e) => setSelectedDealershipId(e.target.value)}
                style={{
                  padding: '10px 16px',
                  paddingRight: '40px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.08)',
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='rgba(255,255,255,0.6)' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  minWidth: '250px'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
              >
                {userDealerships.map(d => (
                  <option key={d.id} value={d.id} style={{ background: '#1a1a1a', padding: '8px' }}>{d.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="content-card">
          <h2 className="card-title">Alerts</h2>
          {todaysDailyLog ? (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              padding: '12px',
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(76, 175, 80, 0.3)'
            }}>
              <div style={{ 
                fontSize: '20px',
                color: '#4caf50'
              }}>✓</div>
              <div>
                <div style={{ 
                  color: '#4caf50',
                  fontWeight: '600',
                  marginBottom: '4px'
                }}>Daily Log Complete</div>
                <div style={{ 
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '13px'
                }}>
                  {todaysDailyLog.greets || 0} greets • {todaysDailyLog.sitDowns || 0} sit-downs • {todaysDailyLog.testRides || 0} test rides
                </div>
              </div>
            </div>
          ) : (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              gap: '12px',
              padding: '12px',
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 152, 0, 0.3)'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  color: '#ff9800',
                  fontWeight: '600',
                  marginBottom: '4px'
                }}>⚠ No Daily Log Today</div>
                <div style={{ 
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '13px'
                }}>Complete your daily log to track your progress</div>
              </div>
              <button
                onClick={() => navigate('/daily?new=true')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ff9800',
                  color: '#000',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#ffb74d'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#ff9800'}
              >
                Log Activity
              </button>
            </div>
          )}
        </div>
          
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap', padding: '0 16px', marginBottom: '24px' }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: '600', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>Time Period</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div className="time-range-selector">
                  <button
                    className={`time-range-btn ${timeRange === 'daily' ? 'active' : ''}`}
                    onClick={() => setTimeRange('daily')}
                  >
                    Daily
                  </button>
                  <button
                    className={`time-range-btn ${timeRange === 'weekly' ? 'active' : ''}`}
                    onClick={() => setTimeRange('weekly')}
                  >
                    Weekly
                  </button>
                  <button
                    className={`time-range-btn ${timeRange === 'monthly' ? 'active' : ''}`}
                    onClick={() => setTimeRange('monthly')}
                  >
                    Monthly
                  </button>
                  <button
                    className={`time-range-btn ${timeRange === 'yearly' ? 'active' : ''}`}
                    onClick={() => setTimeRange('yearly')}
                  >
                    Yearly
                  </button>
                </div>
                {renderMonthPicker()}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '12px', fontWeight: '600', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>View Mode</div>
              <div className="time-range-selector">
                <button
                  className={`time-range-btn ${viewMode === 'personal' ? 'active' : ''}`}
                  onClick={() => setViewMode('personal')}
                >
                  My Targets
                </button>
                <button 
                  className={`time-range-btn ${viewMode === 'dealership' ? 'active' : ''}`}
                  onClick={() => setViewMode('dealership')}
                >
                  Dealership Targets
                </button>
              </div>
            </div>
          </div>

        <div className="kpi-grid">
          {/* Greets */}
          <div className="kpi-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div className="kpi-label">Greets</div>
              <TrendIndicator 
                actual={viewMode === 'personal' ? fitSpecialistKpis.greets.actual : fitSpecialistKpis.greets.dealershipActual}
                target={viewMode === 'personal' ? fitSpecialistKpis.greets.target : fitSpecialistKpis.greets.dealershipTarget}
                fiscalMeta={getFiscalMeta()}
              />
            </div>
            <div className="kpi-value">{formatNumber(viewMode === 'personal' ? fitSpecialistKpis.greets.actual : fitSpecialistKpis.greets.dealershipActual)}</div>
            <div style={{ marginTop: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
              Target: {formatNumber(viewMode === 'personal' ? fitSpecialistKpis.greets.target : fitSpecialistKpis.greets.dealershipTarget)}
            </div>
            <div style={{ marginTop: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
              <div style={{ 
                width: `${calculateProgress(
                  viewMode === 'personal' ? fitSpecialistKpis.greets.actual : fitSpecialistKpis.greets.dealershipActual, 
                  viewMode === 'personal' ? fitSpecialistKpis.greets.target : fitSpecialistKpis.greets.dealershipTarget
                )}%`, 
                height: '100%', 
                background: getProgressColor(calculateProgress(
                  viewMode === 'personal' ? fitSpecialistKpis.greets.actual : fitSpecialistKpis.greets.dealershipActual, 
                  viewMode === 'personal' ? fitSpecialistKpis.greets.target : fitSpecialistKpis.greets.dealershipTarget
                )),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ marginTop: '4px', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
              {calculateProgress(
                viewMode === 'personal' ? fitSpecialistKpis.greets.actual : fitSpecialistKpis.greets.dealershipActual, 
                viewMode === 'personal' ? fitSpecialistKpis.greets.target : fitSpecialistKpis.greets.dealershipTarget
              )}% of target
            </div>
          </div>

          {/* Sit Downs */}
          <div className="kpi-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div className="kpi-label">Sit Downs</div>
              <TrendIndicator 
                actual={viewMode === 'personal' ? fitSpecialistKpis.sitDowns.actual : fitSpecialistKpis.sitDowns.dealershipActual}
                target={viewMode === 'personal' ? fitSpecialistKpis.sitDowns.target : fitSpecialistKpis.sitDowns.dealershipTarget}
                fiscalMeta={getFiscalMeta()}
              />
            </div>
            <div className="kpi-value">{formatNumber(viewMode === 'personal' ? fitSpecialistKpis.sitDowns.actual : fitSpecialistKpis.sitDowns.dealershipActual)}</div>
            <div style={{ marginTop: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
              Target: {formatNumber(viewMode === 'personal' ? fitSpecialistKpis.sitDowns.target : fitSpecialistKpis.sitDowns.dealershipTarget)}
            </div>
            <div style={{ marginTop: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
              <div style={{ 
                width: `${calculateProgress(
                  viewMode === 'personal' ? fitSpecialistKpis.sitDowns.actual : fitSpecialistKpis.sitDowns.dealershipActual, 
                  viewMode === 'personal' ? fitSpecialistKpis.sitDowns.target : fitSpecialistKpis.sitDowns.dealershipTarget
                )}%`, 
                height: '100%', 
                background: getProgressColor(calculateProgress(
                  viewMode === 'personal' ? fitSpecialistKpis.sitDowns.actual : fitSpecialistKpis.sitDowns.dealershipActual, 
                  viewMode === 'personal' ? fitSpecialistKpis.sitDowns.target : fitSpecialistKpis.sitDowns.dealershipTarget
                )),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ marginTop: '4px', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
              {calculateProgress(
                viewMode === 'personal' ? fitSpecialistKpis.sitDowns.actual : fitSpecialistKpis.sitDowns.dealershipActual, 
                viewMode === 'personal' ? fitSpecialistKpis.sitDowns.target : fitSpecialistKpis.sitDowns.dealershipTarget
              )}% of target
            </div>
          </div>

          {/* Test Rides */}
          <div className="kpi-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div className="kpi-label">Test Rides</div>
              <TrendIndicator 
                actual={viewMode === 'personal' ? fitSpecialistKpis.testRides.actual : fitSpecialistKpis.testRides.dealershipActual}
                target={viewMode === 'personal' ? fitSpecialistKpis.testRides.target : fitSpecialistKpis.testRides.dealershipTarget}
                fiscalMeta={getFiscalMeta()}
              />
            </div>
            <div className="kpi-value">{formatNumber(viewMode === 'personal' ? fitSpecialistKpis.testRides.actual : fitSpecialistKpis.testRides.dealershipActual)}</div>
            <div style={{ marginTop: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
              Target: {formatNumber(viewMode === 'personal' ? fitSpecialistKpis.testRides.target : fitSpecialistKpis.testRides.dealershipTarget)}
            </div>
            <div style={{ marginTop: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
              <div style={{ 
                width: `${calculateProgress(
                  viewMode === 'personal' ? fitSpecialistKpis.testRides.actual : fitSpecialistKpis.testRides.dealershipActual, 
                  viewMode === 'personal' ? fitSpecialistKpis.testRides.target : fitSpecialistKpis.testRides.dealershipTarget
                )}%`, 
                height: '100%', 
                background: getProgressColor(calculateProgress(
                  viewMode === 'personal' ? fitSpecialistKpis.testRides.actual : fitSpecialistKpis.testRides.dealershipActual, 
                  viewMode === 'personal' ? fitSpecialistKpis.testRides.target : fitSpecialistKpis.testRides.dealershipTarget
                )),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ marginTop: '4px', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
              {calculateProgress(
                viewMode === 'personal' ? fitSpecialistKpis.testRides.actual : fitSpecialistKpis.testRides.dealershipActual, 
                viewMode === 'personal' ? fitSpecialistKpis.testRides.target : fitSpecialistKpis.testRides.dealershipTarget
              )}% of target
            </div>
          </div>

          {/* Write Ups */}
          <div className="kpi-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div className="kpi-label">Write-Ups</div>
              <TrendIndicator 
                actual={viewMode === 'personal' ? fitSpecialistKpis.writeUps.actual : fitSpecialistKpis.writeUps.dealershipActual}
                target={viewMode === 'personal' ? fitSpecialistKpis.writeUps.target : fitSpecialistKpis.writeUps.dealershipTarget}
                fiscalMeta={getFiscalMeta()}
              />
            </div>
            <div className="kpi-value">{formatNumber(viewMode === 'personal' ? fitSpecialistKpis.writeUps.actual : fitSpecialistKpis.writeUps.dealershipActual)}</div>
            <div style={{ marginTop: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
              Target: {formatNumber(viewMode === 'personal' ? fitSpecialistKpis.writeUps.target : fitSpecialistKpis.writeUps.dealershipTarget)}
            </div>
            <div style={{ marginTop: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
              <div style={{ 
                width: `${calculateProgress(
                  viewMode === 'personal' ? fitSpecialistKpis.writeUps.actual : fitSpecialistKpis.writeUps.dealershipActual, 
                  viewMode === 'personal' ? fitSpecialistKpis.writeUps.target : fitSpecialistKpis.writeUps.dealershipTarget
                )}%`, 
                height: '100%', 
                background: getProgressColor(calculateProgress(
                  viewMode === 'personal' ? fitSpecialistKpis.writeUps.actual : fitSpecialistKpis.writeUps.dealershipActual, 
                  viewMode === 'personal' ? fitSpecialistKpis.writeUps.target : fitSpecialistKpis.writeUps.dealershipTarget
                )),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ marginTop: '4px', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
              {calculateProgress(
                viewMode === 'personal' ? fitSpecialistKpis.writeUps.actual : fitSpecialistKpis.writeUps.dealershipActual, 
                viewMode === 'personal' ? fitSpecialistKpis.writeUps.target : fitSpecialistKpis.writeUps.dealershipTarget
              )}% of target
            </div>
          </div>

          {/* Sales */}
          <div className="kpi-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div className="kpi-label">Sales</div>
              <TrendIndicator 
                actual={viewMode === 'personal' ? fitSpecialistKpis.sales.actual : fitSpecialistKpis.sales.dealershipActual}
                target={viewMode === 'personal' ? fitSpecialistKpis.sales.target : fitSpecialistKpis.sales.dealershipTarget}
                fiscalMeta={getFiscalMeta()}
              />
            </div>
            <div className="kpi-value">{formatNumber(viewMode === 'personal' ? fitSpecialistKpis.sales.actual : fitSpecialistKpis.sales.dealershipActual)}</div>
            <div style={{ marginTop: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
              Target: {formatNumber(viewMode === 'personal' ? fitSpecialistKpis.sales.target : fitSpecialistKpis.sales.dealershipTarget)}
            </div>
            <div style={{ marginTop: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
              <div style={{ 
                width: `${calculateProgress(
                  viewMode === 'personal' ? fitSpecialistKpis.sales.actual : fitSpecialistKpis.sales.dealershipActual, 
                  viewMode === 'personal' ? fitSpecialistKpis.sales.target : fitSpecialistKpis.sales.dealershipTarget
                )}%`, 
                height: '100%', 
                background: getProgressColor(calculateProgress(
                  viewMode === 'personal' ? fitSpecialistKpis.sales.actual : fitSpecialistKpis.sales.dealershipActual, 
                  viewMode === 'personal' ? fitSpecialistKpis.sales.target : fitSpecialistKpis.sales.dealershipTarget
                )),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ marginTop: '4px', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
              {calculateProgress(
                viewMode === 'personal' ? fitSpecialistKpis.sales.actual : fitSpecialistKpis.sales.dealershipActual, 
                viewMode === 'personal' ? fitSpecialistKpis.sales.target : fitSpecialistKpis.sales.dealershipTarget
              )}% of target
            </div>
          </div>
        </div>

        <div className="content-card">
          <h2 className="card-title">Performance Summary</h2>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: '8px 0 0 0', fontSize: '14px' }}>
            Keep up the great work! Track your daily activities to reach your targets.
          </p>
        </div>
      </div>
    );
  }

  // Standard overview for non-Fit Specialist roles
  return (
    <div>
      <div className="view-header" style={{ marginBottom: '16px' }}>
        <h1 className="view-title">Overview</h1>
        <p className="view-subtitle" style={{ marginBottom: 0 }}>
          {user.role === 'owner'
            ? 'Network-wide performance metrics'
            : `Performance metrics for ${user.dealershipName || 'your dealership'}`}
        </p>
      </div>

      {/* Recent Activity - Top Section (owners see activity via notification bell) */}
      {!(isOwnerView) && (
        <div className="content-card">
          <h2 className="card-title">Recent Activity</h2>
          <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Activity feed coming soon...
          </p>
        </div>
      )}

      {/* Filters + time range on one row */}
      <div className="overview-controls-bar">
        {(isOwnerView) ? (
          <div className="overview-controls-left">
            <button
              className={`filter-button ${selectedDealerships.length > 0 ? 'has-filters' : ''}`}
              onClick={() => setShowFilterPanel(true)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 4h18M3 12h12M3 20h6" strokeLinecap="round"/>
              </svg>
              Filters
              {selectedDealerships.length > 0 && ` (${selectedDealerships.length})`}
            </button>
            {selectedDealerships.length > 0 && (
              <div className="active-filters" style={{ paddingTop: 0 }}>
                {dealerships
                  .filter((d) => selectedDealerships.includes(d.id))
                  .map((d) => (
                    <div key={d.id} className="active-filter-chip">
                      {d.name}
                      <button onClick={() => setSelectedDealerships(selectedDealerships.filter(x => x !== d.id))}>×</button>
                    </div>
                  ))}
              </div>
            )}
          </div>
        ) : (
          <div />
        )}
        <div className="overview-time-controls" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div className="time-range-selector" style={{ marginTop: 0 }}>
            <button className={`time-range-btn ${timeRange === 'daily' ? 'active' : ''}`} onClick={() => setTimeRange('daily')}>Daily</button>
            <button className={`time-range-btn ${timeRange === 'weekly' ? 'active' : ''}`} onClick={() => setTimeRange('weekly')}>Weekly</button>
            <button className={`time-range-btn ${timeRange === 'monthly' ? 'active' : ''}`} onClick={() => setTimeRange('monthly')}>Monthly</button>
            <button className={`time-range-btn ${timeRange === 'yearly' ? 'active' : ''}`} onClick={() => setTimeRange('yearly')}>Yearly</button>
          </div>
          {renderMonthPicker()}
        </div>
      </div>

      {/* Columnar layout per dealership for owner view */}
      { (isOwnerView) ? (
        <>
          {/* Filter Panel */}
          {showFilterPanel && (
            <>
              <div className="filter-overlay open" onClick={() => setShowFilterPanel(false)} />
              <div className="filter-panel open">
                <div className="filter-panel-header">
                  <h3>Filters</h3>
                  <button className="filter-panel-close" onClick={() => setShowFilterPanel(false)}>×</button>
                </div>

                <div className="filter-panel-content">
                  <div className="filter-section">
                    <div className="filter-section-header">
                      <h4>Dealerships</h4>
                      <button onClick={() => setSelectedDealerships(selectedDealerships.length === 0 ? dealerships.map(d => d.id) : [])} className="btn-select-all">
                        {selectedDealerships.length === 0 ? 'Select All' : 'Clear All'}
                      </button>
                    </div>
                    {(dealershipsMeta.length ? dealershipsMeta : dealerships).map(dealership => (
                      <label key={dealership.id} className="filter-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedDealerships.includes(dealership.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDealerships([...selectedDealerships, dealership.id]);
                            } else {
                              setSelectedDealerships(selectedDealerships.filter(d => d !== dealership.id));
                            }
                          }}
                        />
                        <span>{dealership.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="kpi-compare-row">
            {(() => {
              const filtered = selectedDealerships.length
                ? dealerships.filter(d => selectedDealerships.includes(d.id))
                : (dealershipsMeta.length ? dealerships.filter(d => (dealershipsMeta.find(m => m.id === d.id)?.status === 'ACTIVE')) : dealerships);

              // Sort by displayOrder (from dealershipsMeta) then by name
              const sorted = [...filtered].sort((a, b) => {
                const aOrder = dealershipsMeta.find(m => m.id === a.id)?.displayOrder ?? 0;
                const bOrder = dealershipsMeta.find(m => m.id === b.id)?.displayOrder ?? 0;
                if (aOrder !== bOrder) return aOrder - bOrder;
                return (a.name || '').localeCompare(b.name || '');
              });

              if (sorted.length === 0) {
                return <div style={{color: 'white', padding: '20px'}}>No dealerships to display</div>;
              }

              return sorted.map((d) => {
                // Find matching dealership metrics from analytics response
                const metrics = dealershipMetrics.find(dm => dm.dealershipId === d.id);

                console.log(`Overview: Dealership ${d.name} (${d.id})`);
                console.log('  - metrics found:', !!metrics);
                if (metrics) {
                  console.log('  - metrics.sales:', metrics.sales);
                  console.log('  - metrics.sales.newUnits:', metrics.sales?.newUnits);
                  console.log('  - metrics.sales.usedUnits:', metrics.sales?.usedUnits);
                }

                if (!metrics) {
                  return (
                    <div key={`ov-${d.id}`} className="dealership-column">
                      <div className="kpi-dealer-label">{d.name}</div>
                      <div style={{color: 'rgba(255,255,255,0.6)', textAlign: 'center', padding: '20px'}}>
                        No data available
                      </div>
                    </div>
                  );
                }

              // Get targets for this dealership
              const dealershipTargetData = dealershipTargets[d.id] || { frontEnd: {}, fixedOps: {} };
              const frontEnd = dealershipTargetData.frontEnd || {};
              const fixedOps = dealershipTargetData.fixedOps || {};

              const dealershipHasTargets = (
                (Array.isArray(frontEnd.monthlyTargets) && frontEnd.monthlyTargets.length > 0) ||
                (Array.isArray(fixedOps.monthlyTargets) && fixedOps.monthlyTargets.length > 0)
              );
              if ((d.id in dealershipTargets) && !dealershipHasTargets) {
                return (
                  <div key={`ov-${d.id}`} className="dealership-column">
                    <div className="kpi-dealer-label">{d.name}</div>
                    <NoTargetsPlaceholder compact />
                  </div>
                );
              }

              // Calculate fiscal year metadata for this dealership
              const fiscalMeta = calculateFiscalMeta(d.id);

              // Sales metrics with dynamic goals based on monthly targets
              // Calculate sales targets from monthlyTargets array
              const calculateSalesTargetsFromMonthly = (monthlyTargets, timeRange, fiscalMeta) => {
                if (!monthlyTargets || monthlyTargets.length === 0) {
                  return {
                    newUnits: 0,
                    usedUnits: 0,
                    totalUnits: 0,
                    newGrossProfit: 0,
                    usedGrossProfit: 0,
                    backEndGrossProfit: 0,
                    fiGrossProfit: 0,
                    totalGrossProfit: 0
                  };
                }

                const totals = monthlyTargets.reduce((acc, month) => ({
                  newUnits: acc.newUnits + (month.newUnits || 0),
                  usedUnits: acc.usedUnits + (month.usedUnits || 0),
                  newGrossProfit: acc.newGrossProfit + (month.newGrossProfit || 0),
                  usedGrossProfit: acc.usedGrossProfit + (month.usedGrossProfit || 0),
                  backEndGrossProfit: acc.backEndGrossProfit + (month.backEndGrossProfit || 0),
                  fiGrossProfit: acc.fiGrossProfit + (month.fiGrossProfit || 0)
                }), { newUnits: 0, usedUnits: 0, newGrossProfit: 0, usedGrossProfit: 0, backEndGrossProfit: 0, fiGrossProfit: 0 });

                // For yearly: sum all months
                if (timeRange === 'yearly') {
                  return {
                    newUnits: totals.newUnits,
                    usedUnits: totals.usedUnits,
                    totalUnits: totals.newUnits + totals.usedUnits,
                    newGrossProfit: totals.newGrossProfit,
                    usedGrossProfit: totals.usedGrossProfit,
                    backEndGrossProfit: totals.backEndGrossProfit,
                    fiGrossProfit: totals.fiGrossProfit,
                    totalGrossProfit: totals.newGrossProfit + totals.usedGrossProfit + totals.backEndGrossProfit
                  };
                }

                // For monthly: use specific month's target
                if (timeRange === 'monthly') {
                  // Use the selected month so looking back at a past month shows
                  // that month's target instead of the current calendar month.
                  const currentMonth = selectedMonth.getMonth(); // 0-based
                  const currentMonthData = monthlyTargets.find(m => m.month === currentMonth + 1);

                  if (!currentMonthData) {
                    return {
                      newUnits: 0,
                      usedUnits: 0,
                      totalUnits: 0,
                      newGrossProfit: 0,
                      usedGrossProfit: 0,
                      backEndGrossProfit: 0,
                      fiGrossProfit: 0,
                      totalGrossProfit: 0
                    };
                  }

                  return {
                    newUnits: currentMonthData.newUnits || 0,
                    usedUnits: currentMonthData.usedUnits || 0,
                    totalUnits: (currentMonthData.newUnits || 0) + (currentMonthData.usedUnits || 0),
                    newGrossProfit: currentMonthData.newGrossProfit || 0,
                    usedGrossProfit: currentMonthData.usedGrossProfit || 0,
                    backEndGrossProfit: currentMonthData.backEndGrossProfit || 0,
                    fiGrossProfit: currentMonthData.fiGrossProfit || 0,
                    totalGrossProfit: (currentMonthData.newGrossProfit || 0) + (currentMonthData.usedGrossProfit || 0) + (currentMonthData.backEndGrossProfit || 0)
                  };
                }

                // For weekly: current month's target / weeks in current month
                if (timeRange === 'weekly') {
                  const now = new Date();
                  const currentMonth = now.getMonth(); // 0-based
                  const currentYear = now.getFullYear();
                  const currentMonthData = monthlyTargets.find(m => m.month === currentMonth + 1);

                  if (!currentMonthData) {
                    return {
                      newUnits: 0,
                      usedUnits: 0,
                      totalUnits: 0,
                      newGrossProfit: 0,
                      usedGrossProfit: 0,
                      backEndGrossProfit: 0,
                      fiGrossProfit: 0,
                      totalGrossProfit: 0
                    };
                  }

                  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
                  const weeksInMonth = daysInMonth / 7;

                  return {
                    newUnits: Math.round((currentMonthData.newUnits || 0) / weeksInMonth),
                    usedUnits: Math.round((currentMonthData.usedUnits || 0) / weeksInMonth),
                    totalUnits: Math.round(((currentMonthData.newUnits || 0) + (currentMonthData.usedUnits || 0)) / weeksInMonth),
                    newGrossProfit: Math.round((currentMonthData.newGrossProfit || 0) / weeksInMonth),
                    usedGrossProfit: Math.round((currentMonthData.usedGrossProfit || 0) / weeksInMonth),
                    backEndGrossProfit: Math.round((currentMonthData.backEndGrossProfit || 0) / weeksInMonth),
                    fiGrossProfit: Math.round((currentMonthData.fiGrossProfit || 0) / weeksInMonth),
                    totalGrossProfit: Math.round(((currentMonthData.newGrossProfit || 0) + (currentMonthData.usedGrossProfit || 0) + (currentMonthData.backEndGrossProfit || 0)) / weeksInMonth)
                  };
                }

                // For daily: current month's target / days in current month
                if (timeRange === 'daily') {
                  const now = new Date();
                  const currentMonth = now.getMonth(); // 0-based
                  const currentYear = now.getFullYear();
                  const currentMonthData = monthlyTargets.find(m => m.month === currentMonth + 1);

                  if (!currentMonthData) {
                    return {
                      newUnits: 0,
                      usedUnits: 0,
                      totalUnits: 0,
                      newGrossProfit: 0,
                      usedGrossProfit: 0,
                      backEndGrossProfit: 0,
                      fiGrossProfit: 0,
                      totalGrossProfit: 0
                    };
                  }

                  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

                  return {
                    newUnits: Math.round((currentMonthData.newUnits || 0) / daysInMonth),
                    usedUnits: Math.round((currentMonthData.usedUnits || 0) / daysInMonth),
                    totalUnits: Math.round(((currentMonthData.newUnits || 0) + (currentMonthData.usedUnits || 0)) / daysInMonth),
                    newGrossProfit: Math.round((currentMonthData.newGrossProfit || 0) / daysInMonth),
                    usedGrossProfit: Math.round((currentMonthData.usedGrossProfit || 0) / daysInMonth),
                    backEndGrossProfit: Math.round((currentMonthData.backEndGrossProfit || 0) / daysInMonth),
                    fiGrossProfit: Math.round((currentMonthData.fiGrossProfit || 0) / daysInMonth),
                    totalGrossProfit: Math.round(((currentMonthData.newGrossProfit || 0) + (currentMonthData.usedGrossProfit || 0) + (currentMonthData.backEndGrossProfit || 0)) / daysInMonth)
                  };
                }

                return {
                  newUnits: 0,
                  usedUnits: 0,
                  totalUnits: 0,
                  newGrossProfit: 0,
                  usedGrossProfit: 0,
                  backEndGrossProfit: 0,
                  fiGrossProfit: 0,
                  totalGrossProfit: 0
                };
              };

              const salesTargets = calculateSalesTargetsFromMonthly(frontEnd.monthlyTargets, timeRange, fiscalMeta);

              const salesProfitGoal = salesTargets.totalGrossProfit;
              const salesUnitsGoal = salesTargets.totalUnits;
              const newUnitsGoal = salesTargets.newUnits;
              const usedUnitsGoal = salesTargets.usedUnits;
              const fiIncomeGoal = salesTargets.fiGrossProfit;

              const salesProfit = metrics.sales?.grossProfit || 0;
              const salesRevenue = metrics.sales?.revenue || 0;
              const totalUnits = metrics.sales?.totalUnits || 0;
              const newUnits = metrics.sales?.newUnits || 0;
              const usedUnits = metrics.sales?.usedUnits || 0;
              const fiIncome = metrics.sales?.fiIncome || 0;
              const salesProfitPct = metrics.sales?.profitMargin || 0;
              const marginTargetRaw = frontEnd.marginTargetPct
                ?? frontEnd.marginTarget
                ?? frontEnd.profitMarginTarget
                ?? frontEnd.profitMargin
                ?? frontEnd.margin;
              const marginTarget = typeof marginTargetRaw === 'string' ? parseFloat(marginTargetRaw) : marginTargetRaw;
              const hasMarginTarget = !Number.isNaN(marginTarget) && marginTarget > 0;

              // Fixed Ops metrics with dynamic goals
              // Fixed ops targets are now stored in monthlyTargets array
              const calculateFixedOpsTargetsFromMonthly = (monthlyTargets, timeRange) => {
                if (!monthlyTargets || monthlyTargets.length === 0) {
                  return {
                    totalGrossProfit: 0,
                    serviceNetSales: 0,
                    partsNetSales: 0,
                    apparelNetSales: 0
                  };
                }

                // For yearly: sum all 12 months
                if (timeRange === 'yearly') {
                  const totals = monthlyTargets.reduce((acc, month) => ({
                    totalGrossProfit: acc.totalGrossProfit + (month.partsGrossProfit || 0) + (month.serviceGrossProfit || 0) + (month.apparelGrossProfit || 0),
                    serviceNetSales: acc.serviceNetSales + (month.serviceNetSales || 0),
                    partsNetSales: acc.partsNetSales + (month.partsNetSales || 0),
                    apparelNetSales: acc.apparelNetSales + (month.apparelNetSales || 0)
                  }), { totalGrossProfit: 0, serviceNetSales: 0, partsNetSales: 0, apparelNetSales: 0 });
                  return totals;
                }

                // For monthly: use specific month's target
                if (timeRange === 'monthly') {
                  // Use the selected month so looking back at a past month shows
                  // that month's target instead of the current calendar month.
                  const currentMonth = selectedMonth.getMonth(); // 0-based
                  const currentMonthData = monthlyTargets.find(m => m.month === currentMonth + 1);

                  if (!currentMonthData) {
                    return {
                      totalGrossProfit: 0,
                      serviceNetSales: 0,
                      partsNetSales: 0,
                      apparelNetSales: 0
                    };
                  }

                  return {
                    totalGrossProfit: (currentMonthData.partsGrossProfit || 0) + (currentMonthData.serviceGrossProfit || 0) + (currentMonthData.apparelGrossProfit || 0),
                    serviceNetSales: currentMonthData.serviceNetSales || 0,
                    partsNetSales: currentMonthData.partsNetSales || 0,
                    apparelNetSales: currentMonthData.apparelNetSales || 0
                  };
                }

                // For weekly: current month's target / weeks in current month
                if (timeRange === 'weekly') {
                  const now = new Date();
                  const currentMonth = now.getMonth(); // 0-based
                  const currentYear = now.getFullYear();
                  const currentMonthData = monthlyTargets.find(m => m.month === currentMonth + 1);

                  if (!currentMonthData) {
                    return {
                      totalGrossProfit: 0,
                      serviceNetSales: 0,
                      partsNetSales: 0,
                      apparelNetSales: 0
                    };
                  }

                  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
                  const weeksInMonth = daysInMonth / 7;

                  return {
                    totalGrossProfit: Math.round(((currentMonthData.partsGrossProfit || 0) + (currentMonthData.serviceGrossProfit || 0) + (currentMonthData.apparelGrossProfit || 0)) / weeksInMonth),
                    serviceNetSales: Math.round((currentMonthData.serviceNetSales || 0) / weeksInMonth),
                    partsNetSales: Math.round((currentMonthData.partsNetSales || 0) / weeksInMonth),
                    apparelNetSales: Math.round((currentMonthData.apparelNetSales || 0) / weeksInMonth)
                  };
                }

                // For daily: current month's target / days in current month
                if (timeRange === 'daily') {
                  const now = new Date();
                  const currentMonth = now.getMonth(); // 0-based
                  const currentYear = now.getFullYear();
                  const currentMonthData = monthlyTargets.find(m => m.month === currentMonth + 1);

                  if (!currentMonthData) {
                    return {
                      totalGrossProfit: 0,
                      serviceNetSales: 0,
                      partsNetSales: 0,
                      apparelNetSales: 0
                    };
                  }

                  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

                  return {
                    totalGrossProfit: Math.round(((currentMonthData.partsGrossProfit || 0) + (currentMonthData.serviceGrossProfit || 0) + (currentMonthData.apparelGrossProfit || 0)) / daysInMonth),
                    serviceNetSales: Math.round((currentMonthData.serviceNetSales || 0) / daysInMonth),
                    partsNetSales: Math.round((currentMonthData.partsNetSales || 0) / daysInMonth),
                    apparelNetSales: Math.round((currentMonthData.apparelNetSales || 0) / daysInMonth)
                  };
                }

                return { totalGrossProfit: 0, serviceNetSales: 0, partsNetSales: 0, apparelNetSales: 0 };
              };

              const fixedOpsTargets = calculateFixedOpsTargetsFromMonthly(fixedOps.monthlyTargets, timeRange);

              const fixedOpsGPGoal = fixedOpsTargets.totalGrossProfit;
              const serviceRevenueGoal = fixedOpsTargets.serviceNetSales;
              const partsRevenueGoal = fixedOpsTargets.partsNetSales;
              const apparelRevenueGoal = fixedOpsTargets.apparelNetSales;

              const fixedOpsGP = metrics.fixedOps?.grossProfit || 0;
              const fixedOpsRevenue = metrics.fixedOps?.totalRevenue || 0;
              const serviceRevenue = metrics.fixedOps?.serviceRevenue || 0;
              const partsRevenue = metrics.fixedOps?.partsRevenue || 0;
              const apparelRevenue = metrics.fixedOps?.apparelRevenue || 0;
              const fixedOpsProfitPct = metrics.fixedOps?.profitMargin || 0;

              // Calculate total net sales and goal for Fixed Ops
              const fixedOpsNetSales = serviceRevenue + partsRevenue + apparelRevenue;
              const fixedOpsNetSalesGoal = (fixedOpsTargets.serviceNetSales || 0) + (fixedOpsTargets.partsNetSales || 0) + (fixedOpsTargets.apparelNetSales || 0);

              // Calculate progress percentages
              const calcProgress = (actual, goal) => {
                if (!goal || goal === 0) return 0;
                return Math.min(Math.round((actual / goal) * 100), 100);
              };

              // Determine color class based on progress
              const getProgressClass = (progress) => {
                if (progress >= 80) return 'high';
                if (progress >= 50) return 'mid';
                return 'low';
              };

              // Calculate actual percentages (can exceed 100%)
              const salesProgressActual = salesProfitGoal > 0 ? Math.round((salesProfit / salesProfitGoal) * 100) : 0;
              const fixedOpsProgressActual = fixedOpsGPGoal > 0 ? Math.round((fixedOpsGP / fixedOpsGPGoal) * 100) : 0;

              // Scale to 120% for visual gauge (so 100% target shows at ~83% on gauge, 120% fills completely)
              const salesProgressGauge = Math.min(Math.round((salesProgressActual / 120) * 100), 100);
              const fixedOpsProgressGauge = Math.min(Math.round((fixedOpsProgressActual / 120) * 100), 100);

              // Determine colors based on time-adjusted performance (on track vs behind)
              const getGaugeColor = (actual, target) => {
                if (!target || target === 0) return '#ff6b6b'; // red for no target

                const today = new Date();
                let expectedProgressPercent = 0;
                const actualProgressPercent = (actual / target) * 100;

                switch (timeRange) {
                  case 'daily':
                    expectedProgressPercent = 100;
                    break;
                  case 'weekly':
                    {
                      const dayOfWeek = today.getDay();
                      const daysIntoWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                      expectedProgressPercent = ((daysIntoWeek + 1) / 7) * 100;
                    }
                    break;
                  case 'monthly':
                    // For a completed past month the full month has elapsed (100% expected);
                    // for the current month, pace against days elapsed. Matches
                    // calculateTrendIndicator so the gauge and the trend arrow agree.
                    if (isHistoricalMonth()) {
                      expectedProgressPercent = 100;
                    } else {
                      const daysIntoMonth = today.getDate();
                      const daysInCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
                      expectedProgressPercent = (daysIntoMonth / daysInCurrentMonth) * 100;
                    }
                    break;
                  case 'yearly':
                    {
                      const fiscalMeta = getFiscalMeta();
                      expectedProgressPercent = (fiscalMeta.daysElapsed / fiscalMeta.daysInYear) * 100;
                    }
                    break;
                  default:
                    expectedProgressPercent = 100;
                }

                const performanceRatio = actualProgressPercent / expectedProgressPercent;
                if (performanceRatio >= 0.9) return '#00d46f'; // green - on track
                if (performanceRatio >= 0.7) return '#ffa500'; // yellow/orange - caution
                return '#ff6b6b'; // red - behind
              };

              const salesGaugeColor = getGaugeColor(salesProfit, salesProfitGoal);
              const fixedOpsGaugeColor = getGaugeColor(fixedOpsGP, fixedOpsGPGoal);
              
              // Keep old calcProgress for sub-metrics
              const salesProgress = calcProgress(salesProfit, salesProfitGoal);
              const fixedOpsProgress = calcProgress(fixedOpsGP, fixedOpsGPGoal);

              return (
                <div key={`ov-${d.id}`} className="dealership-column">
                  <div className="kpi-dealer-label">{d.name}</div>
                  <div className="dealer-main-gauges">
                    {/* Sales Gauge - clickable */}
                    <div
                      className="main-gauge-card clickable"
                      onClick={() => navigate(`/sales-performance?dealershipId=${d.id}`, { state: { dealershipId: d.id } })}
                      title="View Sales Performance Dashboard"
                      style={{ cursor: 'pointer' }}
                    >
                      <h3 className="gauge-title">Sales Performance</h3>
                      <div className="gauge-wrapper">
                        <SemiCircleGauge 
                          value={salesProgressGauge} 
                          size={160}
                          strokeWidth={14}
                          color={salesGaugeColor}
                        >
                          <div className="gauge-center">
                            <div className="gauge-percentage">{salesProgressActual}%</div>
                            <div className="gauge-label">Profit to Goal</div>
                          </div>
                        </SemiCircleGauge>
                      </div>
                      <div className="gauge-details">
                        <div className="gauge-actual">
                          <span className="gauge-detail-label">GP</span>
                          <span className="gauge-detail-value">${abbreviateNumber(salesProfit)} / ${abbreviateNumber(salesProfitGoal)}</span>
                        </div>
                        <div className="gauge-goal">
                          <span className="gauge-detail-label">Margin</span>
                          <span className="gauge-detail-value">
                            {salesProfitPct.toFixed(1)}%
                            {hasMarginTarget ? ` / ${marginTarget.toFixed(1)}%` : ''}
                          </span>
                        </div>
                      </div>
                      {/* Sales sub-metrics */}
                      <div className="sub-metrics">
                        <div className="sub-metric">
                          <div className="sub-metric-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span className="sub-metric-label">Total Units</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span className="sub-metric-value">{totalUnits} / {salesUnitsGoal}</span>
                              <TrendIndicator actual={totalUnits} target={salesUnitsGoal} fiscalMeta={fiscalMeta} />
                            </div>
                          </div>
                          <div className="progress-bar-container">
                            <div 
                              className={`progress-bar-fill ${getProgressClass(calcProgress(totalUnits, salesUnitsGoal))}`}
                              style={{ width: `${calcProgress(totalUnits, salesUnitsGoal)}%` }}
                            />
                          </div>
                        </div>

                        <div className="sub-metric">
                          <div className="sub-metric-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span className="sub-metric-label">New Units</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span className="sub-metric-value">{newUnits} / {newUnitsGoal}</span>
                              <TrendIndicator actual={newUnits} target={newUnitsGoal} fiscalMeta={fiscalMeta} />
                            </div>
                          </div>
                          <div className="progress-bar-container">
                            <div 
                              className={`progress-bar-fill ${getProgressClass(calcProgress(newUnits, newUnitsGoal))}`}
                              style={{ width: `${calcProgress(newUnits, newUnitsGoal)}%` }}
                            />
                          </div>
                        </div>

                        <div className="sub-metric">
                          <div className="sub-metric-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span className="sub-metric-label">Used Units</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span className="sub-metric-value">{usedUnits} / {usedUnitsGoal}</span>
                              <TrendIndicator actual={usedUnits} target={usedUnitsGoal} fiscalMeta={fiscalMeta} />
                            </div>
                          </div>
                          <div className="progress-bar-container">
                            <div 
                              className={`progress-bar-fill ${getProgressClass(calcProgress(usedUnits, usedUnitsGoal))}`}
                              style={{ width: `${calcProgress(usedUnits, usedUnitsGoal)}%` }}
                            />
                          </div>
                        </div>

                        <div className="sub-metric">
                          <div className="sub-metric-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span className="sub-metric-label">F&I Income</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span className="sub-metric-value">${(fiIncome / 1000).toFixed(0)}k / ${(fiIncomeGoal / 1000).toFixed(0)}k</span>
                              <TrendIndicator actual={fiIncome} target={fiIncomeGoal} fiscalMeta={fiscalMeta} />
                            </div>
                          </div>
                          <div className="progress-bar-container">
                            <div 
                              className={`progress-bar-fill ${getProgressClass(calcProgress(fiIncome, fiIncomeGoal))}`}
                              style={{ width: `${calcProgress(fiIncome, fiIncomeGoal)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Fixed Ops Gauge - clickable */}
                    <div
                      className="main-gauge-card clickable"
                      onClick={() => navigate(`/fixed-ops-performance?dealershipId=${d.id}`, { state: { dealershipId: d.id } })}
                      title="View Fixed Operations Performance Dashboard"
                      style={{ cursor: 'pointer' }}
                    >
                      <h3 className="gauge-title">Fixed Operations</h3>
                      <div className="gauge-wrapper">
                        <SemiCircleGauge 
                          value={fixedOpsProgressGauge} 
                          size={160}
                          strokeWidth={14}
                          color={fixedOpsGaugeColor}
                        >
                          <div className="gauge-center">
                            <div className="gauge-percentage">{fixedOpsProgressActual}%</div>
                            <div className="gauge-label">Profit to Goal</div>
                          </div>
                        </SemiCircleGauge>
                      </div>
                      <div className="gauge-details" style={{ flexDirection: 'column', gap: '8px', alignItems: 'stretch' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span className="gauge-detail-label">Net Sales</span>
                          <span className="gauge-detail-value">${abbreviateNumber(fixedOpsNetSales)} / ${abbreviateNumber(fixedOpsNetSalesGoal)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span className="gauge-detail-label">GP</span>
                          <span className="gauge-detail-value">${abbreviateNumber(fixedOpsGP)} / ${abbreviateNumber(fixedOpsGPGoal)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span className="gauge-detail-label">Margin</span>
                          <span className="gauge-detail-value">{fixedOpsProfitPct.toFixed(1)}%</span>
                        </div>
                      </div>

                      {/* Fixed Ops sub-metrics */}
                      <div className="sub-metrics">
                        <div className="sub-metric">
                          <div className="sub-metric-header">
                            <span className="sub-metric-label">Service Revenue</span>
                            <span className="sub-metric-value">${abbreviateNumber(serviceRevenue)} / ${abbreviateNumber(serviceRevenueGoal)}</span>
                          </div>
                          <div className="progress-bar-container">
                            <div 
                              className={`progress-bar-fill ${getProgressClass(calcProgress(serviceRevenue, serviceRevenueGoal))}`}
                              style={{ width: `${calcProgress(serviceRevenue, serviceRevenueGoal)}%` }}
                            />
                          </div>
                        </div>

                        <div className="sub-metric">
                          <div className="sub-metric-header">
                            <span className="sub-metric-label">Parts Revenue</span>
                            <span className="sub-metric-value">${abbreviateNumber(partsRevenue)} / ${abbreviateNumber(partsRevenueGoal)}</span>
                          </div>
                          <div className="progress-bar-container">
                            <div 
                              className={`progress-bar-fill ${getProgressClass(calcProgress(partsRevenue, partsRevenueGoal))}`}
                              style={{ width: `${calcProgress(partsRevenue, partsRevenueGoal)}%` }}
                            />
                          </div>
                        </div>

                        <div className="sub-metric">
                          <div className="sub-metric-header">
                            <span className="sub-metric-label">Apparel Revenue</span>
                            <span className="sub-metric-value">${abbreviateNumber(apparelRevenue)} / ${abbreviateNumber(apparelRevenueGoal)}</span>
                          </div>
                          <div className="progress-bar-container">
                            <div 
                              className={`progress-bar-fill ${getProgressClass(calcProgress(apparelRevenue, apparelRevenueGoal))}`}
                              style={{ width: `${calcProgress(apparelRevenue, apparelRevenueGoal)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            });
            })()}
          </div>
        </>
      ) : (
        <>
          <PerformanceGauges metrics={kpis} />
        </>
      )}

    </div>
  );
}

export default Overview;
